import React, { useEffect, useRef, useState } from "react";
import lottie from "lottie-web";

/**
 * Иконка подарка — анимированная Noto Emoji (порт GiftIcon из Маськи).
 * Lottie JSON лежат в /gift-anim/<key>.json (лицензия CC BY 4.0).
 * Если анимация не загрузилась — запасная статичная SVG-коробка
 * (в Рублоксе нет twemoji-ассетов, поэтому фолбэк свой).
 */

// Кэш загруженных JSON-анимаций (по url), чтобы не качать повторно.
const _lottieCache = new Map();

function loadLottieData(url) {
  if (_lottieCache.has(url)) return _lottieCache.get(url);
  const p = fetch(url)
    .then((r) => { if (!r.ok) throw new Error("lottie " + r.status); return r.json(); })
    .catch(() => null);
  _lottieCache.set(url, p);
  return p;
}

function GiftBoxFallback({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24">
      <rect x="4" y="10" width="16" height="10" rx="1.5" fill="#e8445a" />
      <rect x="3" y="6.5" width="18" height="4" rx="1.2" fill="#ff7a8c" />
      <rect x="10.6" y="6.5" width="2.8" height="13.5" fill="#ffd166" />
      <path d="M12 6.5C10 3.5 6.5 3.8 6.8 5.7c.2 1.4 3 1.2 5.2.8Zm0 0c2-3 5.5-2.7 5.2-.8-.2 1.4-3 1.2-5.2.8Z"
            fill="none" stroke="#ffd166" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * @param lottie  URL Lottie-анимации подарка (/gift-anim/<key>.json)
 * @param size    диаметр кружка (px)
 * @param bg      показывать ли цветную подложку-кружок
 * @param loop    зациклить анимацию (по умолчанию да)
 */
export default function GiftIcon({ lottie: lottieUrl, size = 48, bg = true, loop = true }) {
  const boxRef = useRef(null);
  const animRef = useRef(null);
  // фолбэк показываем только когда анимация ТОЧНО не загрузилась
  const [fallback, setFallback] = useState(false);
  const inner = Math.round(size * 0.78);

  useEffect(() => {
    let cancelled = false;
    setFallback(false);
    if (animRef.current) { try { animRef.current.destroy(); } catch {} animRef.current = null; }
    if (!lottieUrl) { setFallback(true); return; }

    loadLottieData(lottieUrl).then((data) => {
      if (cancelled) return;
      if (!data) { setFallback(true); return; }
      // контейнер всегда в DOM, но на всякий случай ждём его появления
      let tries = 0;
      const mount = () => {
        if (cancelled) return;
        const el = boxRef.current;
        if (!el) {
          if (tries++ < 20) { requestAnimationFrame(mount); return; }
          setFallback(true);
          return;
        }
        try {
          animRef.current = lottie.loadAnimation({
            container: el,
            renderer: "svg",
            loop,
            autoplay: true,
            animationData: data,
            rendererSettings: { progressiveLoad: true, preserveAspectRatio: "xMidYMid meet" },
          });
        } catch {
          setFallback(true);
        }
      };
      mount();
    });

    return () => {
      cancelled = true;
      if (animRef.current) { try { animRef.current.destroy(); } catch {} animRef.current = null; }
    };
  }, [lottieUrl, loop]);

  return (
    <div style={{
      position: "relative",
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
      background: bg ? "linear-gradient(135deg,#fff,#f1edf7)" : "transparent",
      boxShadow: bg ? "0 2px 8px rgba(0,0,0,0.10)" : "none",
    }}>
      {/* контейнер Lottie — ВСЕГДА в DOM (иначе boxRef.current = null) */}
      <div ref={boxRef} style={{ width: inner, height: inner, display: fallback ? "none" : "block" }} />
      {fallback && <GiftBoxFallback size={Math.round(size * 0.62)} />}
    </div>
  );
}
